import { useState, useCallback, useEffect } from "react";
import { warningToast } from "../../../../../services/toasts";
import { formatLocalDateTimeInput } from "../../../../../utils/dateUtils";
import {
  catalogAccessories,
  catalogProducts,
} from "../../../data/stoneflexCatalog";
import { useQuoteCalculations } from "./useQuoteCalculations";

interface UseQuoteStateProps {
  editingQuotation?: Quotation;
  userProfileEmail?: string;
  clients: ClientProfile[];
}

export function useQuoteState({
  editingQuotation,
  userProfileEmail,
  clients,
}: UseQuoteStateProps) {
  const getProductStock = (id: string) => {
    const product = catalogProducts.find((item) => item.id === id);
    if (!product) return null;
    return product.warehouseStock + product.freeZoneStock;
  };

  const getAccessoryStock = (id: string, name?: string) => {
    const accessory = catalogAccessories.find(
      (item) =>
        item.id === id ||
        (name && (item.reference === name || item.name === name))
    );
    if (!accessory) return null;
    return accessory.stock;
  };

  const [selectedClientId, setSelectedClientId] =
    useState<string>("new-client");
  const [currentQuotation, setCurrentQuotation] = useState<Partial<Quotation>>({
    date: formatLocalDateTimeInput(),
    clientName: "",
    taxId: "",
    cityAddress: "",
    phone: "",
    clientEmail: "",
    advisorEmail: "",
    products: [],
    accessories: [],
    notes: "",
    subtotal: 0,
    total: 0,
    vat19: 0,
    cost: 0,
    shipping: 0,
    others: 0,
  });

  const {
    MAX_DISCOUNT_PERCENT,
    applyExistingAutoAccessoryDiscounts,
    buildAutoAccessories,
  } = useQuoteCalculations(currentQuotation);

  const resetForm = useCallback(() => {
    setSelectedClientId("new-client");
    setCurrentQuotation({
      date: formatLocalDateTimeInput(),
      clientName: "",
      taxId: "",
      cityAddress: "",
      phone: "",
      clientEmail: "",
      advisorEmail: userProfileEmail || "",
      products: [],
      accessories: [],
      notes: "",
      subtotal: 0,
      total: 0,
      vat19: 0,
      cost: 0,
      shipping: 0,
      others: 0,
    });
  }, [userProfileEmail]);

  // Load editing quotation when provided
  useEffect(() => {
    if (editingQuotation) {
      setCurrentQuotation(editingQuotation);
    } else {
      resetForm();
    }
  }, [editingQuotation, resetForm]);

  useEffect(() => {
    if (!userProfileEmail) return;
    if (editingQuotation) return;

    setCurrentQuotation((prev) => {
      if (prev.advisorEmail) return prev;
      return { ...prev, advisorEmail: userProfileEmail };
    });
  }, [userProfileEmail, editingQuotation]);

  const handleSelectClient = (clientId: string) => {
    setSelectedClientId(clientId);

    if (clientId === "" || clientId === "new-client") {
      setCurrentQuotation((prev) => ({
        ...prev,
        clientName: "",
        taxId: "",
        cityAddress: "",
        phone: "",
        clientEmail: "",
      }));
      return;
    }

    const selectedClient = clients.find(
      (client) => String(client.id) === clientId
    );

    if (!selectedClient) return;

    const firstName = (selectedClient.first_name || "").trim();
    const lastName = (selectedClient.last_name || "").trim();
    const fullName = `${firstName} ${lastName}`.trim();
    const city = (selectedClient.city_name || "").trim();
    const street = (selectedClient.street_address || "").trim();
    const neighborhood = (selectedClient.neighborhood || "").trim();
    const fullAddress = [city, street, neighborhood]
      .filter(Boolean)
      .join(" - ");

    setCurrentQuotation((prev) => ({
      ...prev,
      clientName: fullName || "Sin nombre",
      taxId: selectedClient.cedula || "",
      cityAddress: fullAddress,
      phone: selectedClient.phone || "",
      clientEmail: selectedClient.email || "",
    }));
  };

  const handleSelectProduct = (product: QuoteProduct) => {
    const availableStock = getProductStock(product.id);
    if (availableStock !== null && product.quantity > availableStock) {
      warningToast(
        `Cantidad solicitada (${product.quantity} láminas) supera el stock disponible (${availableStock}).`
      );
    }

    setCurrentQuotation((prev) => {
      const products = [...(prev.products || []), product];
      const manualAccessories = (prev.accessories || []).filter(
        (item) => item.source !== "auto"
      );
      const autoAccessories = applyExistingAutoAccessoryDiscounts(
        buildAutoAccessories(products),
        prev.accessories || []
      );

      return {
        ...prev,
        products,
        accessories: [...manualAccessories, ...autoAccessories],
      };
    });
  };

  const handleSelectAccessory = (accessory: any, quantity: number) => {
    const availableStock = getAccessoryStock(accessory.id);
    if (availableStock !== null && quantity > availableStock) {
      warningToast(
        `Cantidad solicitada (${quantity} unidades) supera el stock disponible (${availableStock}).`
      );
    }

    const newAccessory: QuoteProduct = {
      id: accessory.id,
      name: accessory.reference,
      quantity: quantity,
      price: accessory.price,
      discountPercent: 0,
      source: "manual",
    };

    setCurrentQuotation((prev) => ({
      ...prev,
      accessories: [...(prev.accessories || []), newAccessory],
    }));
  };

  const removeProduct = (type: "products" | "accessories", id: string) => {
    setCurrentQuotation((prev) => {
      if (type === "products") {
        const products = prev.products?.filter((p) => p.id !== id) || [];
        const manualAccessories = (prev.accessories || []).filter(
          (item) => item.source !== "auto"
        );
        const autoAccessories = applyExistingAutoAccessoryDiscounts(
          buildAutoAccessories(products),
          prev.accessories || []
        );

        return {
          ...prev,
          products,
          accessories: [...manualAccessories, ...autoAccessories],
        };
      }

      return {
        ...prev,
        [type]: prev[type]?.filter((p) => p.id !== id) || [],
      };
    });
  };

  const updateProductQuantity = (
    type: "products" | "accessories",
    id: string,
    quantity: number
  ) => {
    const normalizedQuantity = Math.max(1, quantity);

    if (type === "products") {
      const availableStock = getProductStock(id);
      if (availableStock !== null && normalizedQuantity > availableStock) {
        warningToast(
          `Cantidad solicitada (${normalizedQuantity} láminas) supera el stock disponible (${availableStock}).`
        );
      }
    } else {
      const currentAccessory = currentQuotation.accessories?.find(
        (accessory) => accessory.id === id
      );
      const availableStock = getAccessoryStock(id, currentAccessory?.name);
      if (availableStock !== null && normalizedQuantity > availableStock) {
        warningToast(
          `Cantidad solicitada (${normalizedQuantity} unidades) supera el stock disponible (${availableStock}).`
        );
      }
    }

    setCurrentQuotation((prev) => {
      if (type === "products") {
        const products =
          prev.products?.map((p) =>
            p.id === id
              ? {
                  ...p,
                  quantity: normalizedQuantity,
                  sheetsNeeded: normalizedQuantity,
                  requestedM2:
                    p.widthCm && p.lengthCm
                      ? Math.round(
                          normalizedQuantity *
                            (p.widthCm / 100) *
                            (p.lengthCm / 100) *
                            100
                        ) / 100
                      : p.requestedM2,
                }
              : p
          ) || [];
        const manualAccessories = (prev.accessories || []).filter(
          (item) => item.source !== "auto"
        );
        const autoAccessories = applyExistingAutoAccessoryDiscounts(
          buildAutoAccessories(products),
          prev.accessories || []
        );

        return {
          ...prev,
          products,
          accessories: [...manualAccessories, ...autoAccessories],
        };
      }

      return {
        ...prev,
        [type]:
          prev[type]?.map((p) =>
            p.id === id ? { ...p, quantity: normalizedQuantity } : p
          ) || [],
      };
    });
  };

  const updateProductDiscount = (id: string, discountPercent: number) => {
    if (discountPercent > MAX_DISCOUNT_PERCENT) {
      warningToast(`El descuento máximo permitido es ${MAX_DISCOUNT_PERCENT}%`);
    }
    const normalizedDiscount = Math.min(
      MAX_DISCOUNT_PERCENT,
      Math.max(0, discountPercent)
    );
    setCurrentQuotation((prev) => ({
      ...prev,
      products:
        prev.products?.map((p) =>
          p.id === id ? { ...p, discountPercent: normalizedDiscount } : p
        ) || [],
    }));
  };

  const updateAccessoryDiscount = (id: string, discountPercent: number) => {
    if (discountPercent > MAX_DISCOUNT_PERCENT) {
      warningToast(`El descuento máximo permitido es ${MAX_DISCOUNT_PERCENT}%`);
    }
    const normalizedDiscount = Math.min(
      MAX_DISCOUNT_PERCENT,
      Math.max(0, discountPercent)
    );
    setCurrentQuotation((prev) => ({
      ...prev,
      accessories:
        prev.accessories?.map((a) =>
          a.id === id ? { ...a, discountPercent: normalizedDiscount } : a
        ) || [],
    }));
  };

  const updateProductMeasure = (
    id: string,
    value: number,
    unit: "m2" | "sheets"
  ) => {
    const currentProduct = currentQuotation.products?.find(
      (product) => product.id === id
    );
    const availableStock = getProductStock(id);

    if (
      currentProduct?.widthCm &&
      currentProduct?.lengthCm &&
      availableStock !== null
    ) {
      if (unit === "m2") {
        const requestedM2 = Math.max(0.1, value);
        const m2PerSheet =
          (currentProduct.widthCm / 100) * (currentProduct.lengthCm / 100);
        const sheetsNeeded = Math.max(1, Math.ceil(requestedM2 / m2PerSheet));

        if (sheetsNeeded > availableStock) {
          warningToast(
            `Cantidad solicitada (${sheetsNeeded} láminas) supera el stock disponible (${availableStock}).`
          );
        }
      } else {
        const sheetsNeeded = Math.max(1, Math.round(value));
        if (sheetsNeeded > availableStock) {
          warningToast(
            `Cantidad solicitada (${sheetsNeeded} láminas) supera el stock disponible (${availableStock}).`
          );
        }
      }
    }

    setCurrentQuotation((prev) => {
      const products =
        prev.products?.map((p) => {
          if (p.id !== id) return p;

          if (unit === "m2") {
            const requestedM2 = Math.max(0.1, value);

            if (p.widthCm && p.lengthCm) {
              const m2PerSheet = (p.widthCm / 100) * (p.lengthCm / 100);
              const sheetsNeeded = Math.max(
                1,
                Math.ceil(requestedM2 / m2PerSheet)
              );
              const calculatedM2 =
                Math.round(sheetsNeeded * m2PerSheet * 100) / 100;

              return {
                ...p,
                quantity: sheetsNeeded,
                sheetsNeeded,
                requestedM2: calculatedM2,
              };
            }

            return {
              ...p,
              requestedM2: Math.round(requestedM2 * 100) / 100,
            };
          }

          const sheetsNeeded = Math.max(1, Math.round(value));
          return {
            ...p,
            quantity: sheetsNeeded,
            sheetsNeeded,
            requestedM2:
              p.widthCm && p.lengthCm
                ? Math.round(
                    sheetsNeeded * (p.widthCm / 100) * (p.lengthCm / 100) * 100
                  ) / 100
                : p.requestedM2,
          };
        }) || [];

      const manualAccessories = (prev.accessories || []).filter(
        (item) => item.source !== "auto"
      );
      const autoAccessories = applyExistingAutoAccessoryDiscounts(
        buildAutoAccessories(products),
        prev.accessories || []
      );

      return {
        ...prev,
        products,
        accessories: [...manualAccessories, ...autoAccessories],
      };
    });
  };

  return {
    selectedClientId,
    setSelectedClientId,
    currentQuotation,
    setCurrentQuotation,
    handleSelectClient,
    handleSelectProduct,
    handleSelectAccessory,
    removeProduct,
    updateProductQuantity,
    updateProductDiscount,
    updateAccessoryDiscount,
    updateProductMeasure,
    resetForm,
    MAX_DISCOUNT_PERCENT,
  };
}
